import {
  CANVAS_ROTATE_CURSOR_HOTSPOT,
  ROTATE_CURSOR_BOTTOM_LEFT_SVG,
  ROTATE_CURSOR_BOTTOM_RIGHT_SVG,
  ROTATE_CURSOR_TOP_LEFT_SVG,
  ROTATE_CURSOR_TOP_RIGHT_SVG,
} from './canvasRotateCursorArt'
import type { ResizeCorner } from '../utils/canvasAlignment'

/**
 * Die vier **Dreh-Cursor**, fertig als Wert für `cursor`.
 *
 * Sie stehen hier und nicht in der SCSS, weil ein `data:`-URL DATEN sind — wie
 * die Fläche eines Zettels (`--note-fill`) und das Bild eines Streifens
 * (`--tape-image`). Das Rezept (`cursor: var(…)`) bleibt in
 * `RotateHandles.scss`.
 *
 * Siehe „Neigung" in CONTEXT.md.
 */

/**
 * Ein SVG als `data:`-URL.
 *
 * `encodeURIComponent` und nicht Base64: Der Text bleibt lesbar, das Ergebnis
 * ist kürzer, und ein `#` im Bild (Farbwerte!) bricht die URL sonst — es
 * beendete den Pfad und alles danach wäre ein Fragment. Dieselbe Rechnung wie
 * bei den **Klebestreifen**.
 */
const toDataUrl = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

/**
 * Bild, Hotspot und **Rückfall** in einem Wert.
 *
 * Der Rückfall ist keine Höflichkeit, sondern Pflicht: Chromium verlangt am
 * Ende einer Bild-Cursor-Liste ein Schlüsselwort, sonst fällt die ganze
 * Deklaration weg. `alias` ist auf beiden Systemen ein gebogener Pfeil — das
 * Nächstbeste, wenn das Bild nicht kommt.
 */
const cursorValue = (svg: string): string =>
  `url("${toDataUrl(svg)}") ${CANVAS_ROTATE_CURSOR_HOTSPOT} ${CANVAS_ROTATE_CURSOR_HOTSPOT}, alias`

/** Der Cursor je Ecke — dieselben vier Ecken wie beim Skalieren. */
export const CANVAS_ROTATE_CURSORS: Record<ResizeCorner, string> = {
  'top-left': cursorValue(ROTATE_CURSOR_TOP_LEFT_SVG),
  'top-right': cursorValue(ROTATE_CURSOR_TOP_RIGHT_SVG),
  'bottom-left': cursorValue(ROTATE_CURSOR_BOTTOM_LEFT_SVG),
  'bottom-right': cursorValue(ROTATE_CURSOR_BOTTOM_RIGHT_SVG),
}

/**
 * Der Cursor für einen Zeiger, der **irgendwo** über einem drehbaren Ding
 * steht — die Ecke ergibt sich aus seiner Lage zur **Drehmitte**.
 *
 * Gebraucht vom **Klebestreifen**: Er wird nicht an vier Zonen gegriffen,
 * sondern auf seiner ganzen Fläche (er ist 128×36 groß und liegt mit seinen
 * Ecken auf dem Papier — Zonen dort wären kleiner als der Zeiger). Trotzdem
 * soll er dieselben vier Richtungen zeigen wie ein Zettel: Ein Cursor, der
 * links unten dasselbe behauptet wie rechts oben, deutet in die Hälfte der
 * Fälle falsch herum.
 *
 * **In Bildschirm-Koordinaten**, nicht im Rahmen des gedrehten Streifens: Das
 * Bild des Cursors liegt auf dem Schirm, und dort muss es stimmen.
 *
 * Die Grenze läuft genau durch die Mitte; wer auf ihr steht, bekommt die
 * rechte bzw. untere Seite (`>= 0`). Ein Sonderfall dafür wäre ein Cursor, der
 * auf einer unsichtbaren Linie flackert.
 */
export function canvasRotateCursorAt(
  center: { x: number; y: number },
  point: { x: number; y: number },
): string {
  const rechts = point.x - center.x >= 0
  const unten = point.y - center.y >= 0
  return CANVAS_ROTATE_CURSORS[
    unten
      ? (rechts ? 'bottom-right' : 'bottom-left')
      : (rechts ? 'top-right' : 'top-left')
  ]
}
